// admin-ui/src/components/VolumeChart.tsx
// ALL RIGHTS RESERVED

/**
 * Volume Chart
 * Simple bar chart for daily transaction volume
 * Uses pure CSS - no external chart library required
 */

import { useMemo } from 'react';
import { DailyVolume } from '../api/client';

// ═══════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════

interface VolumeChartProps {
  data: DailyVolume[];
}

// ═══════════════════════════════════════════════════════
// HELPERS
// ═══════════════════════════════════════════════════════

function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function formatCurrency(value: string | number): string {
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (num >= 1000000) {
    return `$${(num / 1000000).toFixed(1)}M`;
  }
  if (num >= 1000) {
    return `$${(num / 1000).toFixed(1)}K`;
  }
  return `$${num.toFixed(0)}`;
}

// ═══════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════

export function VolumeChart({ data = [] }: VolumeChartProps) {
  // Calculate max for scaling
  const maxVolume = useMemo(() => {
    return Math.max(...data.map(d => parseFloat(d.volume)), 1);
  }, [data]);

  // Calculate totals
  const totals = useMemo(() => {
    return data.reduce(
      (acc, d) => ({
        volume: acc.volume + parseFloat(d.volume),
        fees: acc.fees + parseFloat(d.fees),
        txCount: acc.txCount + d.txCount,
      }),
      { volume: 0, fees: 0, txCount: 0 }
    );
  }, [data]);

  if (data.length === 0) {
    return (
      <div className="volume-chart empty">
        <p>No transaction data available</p>
      </div>
    );
  }

  return (
    <div className="volume-chart">
      <div className="chart-summary">
        <div className="summary-item">
          <label>Total Volume</label>
          <span>{formatCurrency(totals.volume)}</span>
        </div>
        <div className="summary-item">
          <label>Total Fees</label>
          <span>{formatCurrency(totals.fees)}</span>
        </div>
        <div className="summary-item">
          <label>Transactions</label>
          <span>{totals.txCount.toLocaleString()}</span>
        </div>
      </div>

      <div className="chart-container">
        <div className="chart-bars">
          {data.map((day, index) => {
            const height = (parseFloat(day.volume) / maxVolume) * 100;
            return (
              <div 
                key={day.date}
                className="bar-wrapper"
                title={`${day.date}\nVolume: ${formatCurrency(day.volume)}\nFees: ${formatCurrency(day.fees)}\nTxns: ${day.txCount}`}
              >
                <div 
                  className="bar"
                  style={{ height: `${Math.max(height, 2)}%` }}
                />
                {index % Math.ceil(data.length / 7) === 0 && (
                  <span className="bar-label">{formatDate(day.date)}</span>
                )}
              </div>
            );
          })}
        </div>
        <div className="chart-axis">
          <span>{formatCurrency(maxVolume)}</span>
          <span>{formatCurrency(maxVolume / 2)}</span>
          <span>$0</span>
        </div>
      </div>
    </div>
  );
}

export default VolumeChart;

